<template>
  <div class="view-container">
    <div class="view-gabarit">
      <div class="view-row">
        <div class="view-description">
          <p class="baseline">{{ $t('fileCollaboratorAddress') }}</p>
          <p class="description">{{ $t('csvFile') }}</p>
        </div>
        <div class="view-content">
          <VeeForm v-slot="{ errors }" enctype="multipart/form-data" @submit="validateFile">
            <fieldset class="large">
              <div v-if="!isIE" class="input-file" :class="{ dragging: dragging }">
                <div v-if="!fileUpload" class="upload-container">
                  <Field rules="required|ext:csv|size:10240" name="csvInput">
                    <input
                      ref="csvInput"
                      accept=".csv"
                      type="file"
                      class="upload-input"
                      :class="{ 'has-error': errors.csvInput }"
                      @change="uploadFile($event.target.files)"
                      @dragenter="dragging = true"
                      @dragend="dragging = false"
                      @dragleave="dragging = false"
                    />
                  </Field>
                  <i class="icon-plusdownload add-icon"></i>
                  <div class="input-text">
                    {{ $t('dargAndDropFile') }}
                    <div class="select">
                      {{ $t('chooseFile') }}
                    </div>
                  </div>
                </div>
                <div v-if="fileUpload && !progress" class="file-container" :class="{ 'collaborator-error': collaboratorError }">
                  <i class="icon-csvfile document-icon"></i>
                  <div class="info-container">
                    <div class="document-title">{{ fileUpload.name }}</div>
                    <div class="delete-button" @click="deleteFile">{{ $t('delete') }}</div>
                  </div>
                </div>
                <div v-if="fileUpload && progress" class="progress-container">
                  <div class="progress-title">{{ $t('fileLoading') }}</div>
                  <div class="progress-value">{{ progress }}%</div>
                  <div class="progress-bar">
                    <div class="progressing" :style="{ width: `${Math.min(progress, 100)}%` }"></div>
                  </div>
                </div>
              </div>

              <Field v-else rules="required|ext:csv|size:10240" name="csvInput">
                <input
                  ref="csvInput"
                  accept=".csv"
                  type="file"
                  class="upload-input input-ie"
                  :class="{ 'has-error': errors.csvInput }"
                  @change="uploadFile($event.target.files)"
                  @dragenter="dragging = true"
                  @dragend="dragging = false"
                  @dragleave="dragging = false"
                />
              </Field>

              <span class="error-caption">{{ errors.csvInput }}</span>
              <div v-if="error" class="error-container">{{ `${$t('csvImportError')} ${error}` }}</div>
              <div v-if="collaboratorError && collaboratorError.length > 0" class="error-container">
                <div class="error-message-container">
                  <b>{{ $t('ImportError') }}</b>
                </div>
                <div v-if="businessUnitError" class="error-message-container">{{ $t('ImportBusinessUnitError') }}</div>
                <div v-for="collaborator in collaboratorError" :key="collaborator.id" class="error-message-container">
                  <span class="error-message-collaborator">
                    {{ $t('Collaborator') }}
                    {{ collaborator.jll_identifier || `(${$t('undefinedCollaborator')})` }} :
                  </span>
                  <span class="error-message-detail">
                    {{
                      collaborator.status_message
                        .map(columnInError => (columnInError === 'TransportMode:' ? columnInError + ' empty' : columnInError))
                        .join(', ')
                    }}
                  </span>
                </div>
              </div>
            </fieldset>

            <fieldset>
              <button :class="{ disabled: errors.csvInput || collaboratorError }" class="study-button main" type="submit">
                {{ $t('validate') }}
              </button>
            </fieldset>
          </VeeForm>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Field, Form as VeeForm } from 'vee-validate';

export default {
  components: {
    Field,
    VeeForm,
  },

  data() {
    return {
      files: false,
      dragging: false,
      error: false,
      fileUpload: null,
      progress: 0,
      collaboratorError: null,
      businessUnitError: false,
    };
  },

  computed: {
    ...mapGetters(['rawStudy']),

    isIE() {
      const ua = navigator.userAgent;
      /* MSIE used to detect old browsers and Trident used to newer ones */
      return ua.indexOf('MSIE ') > -1 || ua.indexOf('Trident/') > -1;
    },
  },

  watch: {
    rawStudy() {
      this.setDraftUploadFile();
    },
  },

  mounted() {
    if (this.rawStudy) {
      this.setDraftUploadFile();
    }
  },

  methods: {
    uploadFile(files) {
      this.error = false;
      this.collaboratorError = null;
      if (files.length <= 0) {
        return;
      }

      this.dragging = false;
      this.fileUpload = files[0];
    },

    deleteFile() {
      this.loading = false;
      this.error = false;
      this.fileUpload = null;
      this.collaboratorError = null;
    },

    validateFile() {
      const id = this.$route.params.studyId;
      this.error = false;

      if (!this.fileUpload || !id) {
        console.log('Invalid form');
        return;
      }

      // fileUpload size is negative when we have a 'draft' study with an already uploaded CSV file
      if (this.fileUpload.size < 0) {
        this.$router.push({ name: 'studyCurrentSites' });
      }

      this.loading = true;
      this.$store
        .dispatch('importFile', { id, file: this.fileUpload, onUploadProgress: this.onUploadProgress })
        .then(() => {
          this.$store
            .dispatch('fetchProcessStatus', { id })
            .then(response => {
              this.loading = false;
              this.error = false;
              this.progress = 0;

              if (response.code === 15) {
                this.getCollaboratorError(id);
              } else {
                this.rawStudy.filename = this.fileUpload?.name || '';
                this.fileUpload = null;
                this.$router.push({ name: 'studyUpdateFilters' });
              }
            })
            .catch(e => {
              this.loading = false;
              this.fileUpload = null;
              this.error = e.response.data.error;
              this.progress = 0;
            });
        })
        .catch(e => {
          this.loading = false;
          this.fileUpload = null;
          this.error = e.response.data.error;
          this.progress = 0;
        });
    },

    getCollaboratorError(id) {
      this.$store.dispatch('fetchCollaborators', { id }).then(response => {
        this.collaboratorError = response
          .filter(c => c.status === 2)
          .sort((c1, c2) => {
            return c1.jll_identifier - c2.jll_identifier;
          });
        this.businessUnitError = this.collaboratorError.filter(c => c.status_message.includes('BusinessUnit')).length > 0;
      });
    },

    onUploadProgress(progressEvent) {
      this.progress = Math.min(Math.round((progressEvent.loaded * 100) / progressEvent.total), 100);
    },

    setDraftUploadFile() {
      if (this.rawStudy.is_draft || this.rawStudy.filename) {
        this.fileUpload = {
          name: this.rawStudy.filename,
          size: -1,
        };
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';
@use '@/styles/animations.scss';

.input-file {
  position: relative;
  width: 100%;
  height: 160px;
  margin: 0 auto;
  background: none;
  overflow: hidden;
  transition: 0.4s ease;

  .upload-container {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    border: dashed 2px #e8e7e6;
    box-sizing: border-box;
    border-radius: 4px;
    box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0);
    transition: 0.4s ease;
    cursor: pointer;

    .add-icon {
      font-size: 48px;
      color: colors.$main-color;
      margin: 0 16px 0 0;
    }

    .input-text {
      .select {
        font-weight: bold;
        color: colors.$main-color;
        margin: 4px 0 0;
      }
    }

    &:hover,
    &.dragging {
      border-color: colors.$main-color;
      background: #f8f7f7;
    }

    &.error {
      border-color: colors.$red-color;
    }

    input {
      opacity: 0;
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      width: 100%;
      cursor: pointer;
    }
  }

  .file-container {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    border: solid 2px #1daea1;
    background: rgba(29, 174, 161, 0.15);
    color: #3c3c3b;
    box-sizing: border-box;
    border-radius: 4px;
    box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0);
    transition: 0.4s ease;
    cursor: pointer;

    &.collaborator-error {
      border-color: colors.$red-color;
      background: rgba(colors.$red-color, 0.15);
    }

    .document-icon {
      margin: 0;
      font-size: 96px;
    }

    .info-container {
      .document-title {
        font-size: 16px;
        font-weight: 600;
      }
      .delete-button {
        font-size: 14px;
        font-weight: 600;
        color: colors.$main-color;
      }
    }
  }

  .progress-container {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border: dashed 2px #e8e7e6;
    box-sizing: border-box;
    border-radius: 4px;
    box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0);
    transition: 0.4s ease;
    cursor: pointer;

    .progress-title {
      color: #77797f;
      font-size: 16px;
    }
    .progress-value {
      color: #77797f;
      font-size: 28px;
      font-weight: 600;
    }
    .progress-bar {
      width: 80%;
      height: 12px;
      margin: 12px 0 0;
      padding: 2px;
      border: solid 1px #e8e7e6;
      border-radius: 8px;
      box-sizing: border-box;

      .progressing {
        height: 100%;
        width: 0;
        background: #1daea1;
        border-radius: 8px;
        transition: 0.4s ease;
      }
    }
  }
}

.error-container {
  width: 100%;
  margin: 16px auto;
  padding: 4px 16px;
  overflow: hidden;
  line-height: 36px;
  color: colors.$red-color;
  background: rgba(colors.$red-color, 0.1);
  border: solid 2px colors.$red-color;
  border-radius: 4px;
  box-sizing: border-box;
  transition: 0.4s ease;

  .error-message-collaborator {
    margin: 0 8px 0 0;
  }
}
</style>
